import { useState, useEffect } from "react"
import axios from "axios"

export function useSearchLocation(
  apiUrl: string | null, 
  debounceMs = 500, 
  ignoreTerm?: string,
  additionalPayload?: Record<string, any>
) {
  const [searchTerm, setSearchTerm] = useState("")
  const [results, setResults] = useState<any[]>([])
  const [isSearching, setIsSearching] = useState(false)

  // Use a stringified version of additionalPayload to avoid infinite re-renders if the caller passes a new object literal every time.
  const additionalPayloadStr = JSON.stringify(additionalPayload || {})

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!apiUrl || !searchTerm || searchTerm.length < 2) {
        setResults([])
        return
      }
      if (ignoreTerm && searchTerm === ignoreTerm) {
        // user already selected, no need to search
        return
      }
      setIsSearching(true)
      try {
        const payloadToUse = JSON.parse(additionalPayloadStr)
        const response = await axios.post(apiUrl, {
          search: searchTerm,
          ...payloadToUse
        })
        if (response.data.success && response.data.result?.data) {
          const rawData = response.data.result.data
          let formattedData = rawData
          
          // Handle specific response format from Pincode API where pincodes are in an array
          if (rawData.length > 0 && Array.isArray(rawData[0].pincode)) {
            formattedData = rawData.flatMap((item: any) => 
              item.pincode.map((pin: string) => ({
                _id: pin, // Use pin as unique ID
                pincode: pin,
                status: item.status,
                reactivationDate: item.reactivationDate
              }))
            )
          }
          
          setResults(formattedData)
        } else {
          setResults([])
        }
      } catch (error) {
        console.error("Airport search error", error)
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [searchTerm, debounceMs, ignoreTerm, apiUrl, additionalPayloadStr])

  return {
    searchTerm,
    setSearchTerm,
    results,
    setResults,
    isSearching,
  }
}
